<template>
  <b-modal
    id="load-wallet-modal"
    ref="modal"
    title="Load Customer Wallet"
    size="lg"
    hide-footer
    @hidden="resetForm"
  >

    <!-- Search customer -->
    <b-form-group label="Search Customer">
      <b-input-group>
        <b-form-input
          v-model="search"
          placeholder="Type name or phone number..."
          @input="onSearch"
        />
        <b-input-group-append>
          <b-button variant="outline-secondary" @click="onSearch">
            <i class="fas fa-search"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <!-- Search results dropdown -->
    <div v-if="searchResults.length" class="mb-3">
      <div
        v-for="c in searchResults"
        :key="c.id"
        class="border-bottom rounded p-2 mb-1 d-flex justify-content-between align-items-center"
        style="cursor:pointer;"
        @click="selectCustomer(c)"
      >
        <div>
          <strong>{{ c.full_name }}</strong>
          <span class="text-muted ml-2 ">{{ c.phone }}</span>
        </div>
        <b-button size="sm" variant="outline-primary">Select</b-button>
      </div>
    </div>

    <!-- No results -->
    <div v-if="searched && !searchResults.length && search.length > 1" class="text-muted mb-3">
      No customers found for "{{ search }}".
    </div>

    <!-- Customer wallet panel -->
    <div v-if="customer">
      <hr />

      <!-- Customer info + balance -->


            <div class="card">
                <div class="card-body">
                    <h5>{{ customer.full_name }}</h5>
                    <p>{{ customer.phone }}</p>
                    <p v-if="customer.email" class="text-muted  mb-0">{{ customer.email }}</p>
                    <hr />

                    <h5>KES {{ formatAmount(balance) }}</h5>
                     <p >Wallet Balance</p>
                </div>
            </div>

      <!-- Active subscription if any -->
      <div v-if="activeSubscription" class="py-3">
        <h5 class="text-muted">ACTIVE SUBSCRIPTION</h5>
        <b-card class="border-0 bg-light">
          <b-row>
            <b-col>
              <strong>{{ activeSubscription.package.name }}</strong>
              <span class="text-muted ml-2 ">
                Expires {{ formatDate(activeSubscription.end_date, 'DD MMM YYYY') }}
              </span>
            </b-col>
            <b-col cols="auto">
              <b-badge variant="success">Active</b-badge>
            </b-col>
          </b-row>
        </b-card>
      </div>

      <!-- Recent transactions -->
      <div v-if="transactions.length">
        <p class="text-muted  font-weight-bold mb-1">RECENT TRANSACTIONS</p>
        <b-table
          :items="transactions.slice(0, 5)"
          :fields="txFields"

          borderless
          responsive
          stacked="md"
          class="mb-0"
        >
          <template #cell(type)="data">
            <b-badge :variant="data.item.type === 'credit' ? 'success' : 'secondary'">
              {{ data.item.type }}
            </b-badge>
          </template>

          <template #cell(amount)="data">
            <span :class="data.item.type === 'credit' ? 'text-primary' : 'text-secondary'">
              {{ data.item.type === 'credit' ? '+' : '−' }} KES {{ formatAmount(data.item.amount) }}
            </span>
          </template>

          <template #cell(description)="data">
            <span class="text-muted ">{{ data.item.description || '—' }}</span>
          </template>

          <template #cell(created_at)="data">
            <span class="text-muted ">{{ formatDate(data.item.created_at, 'DD-MM-YYYY HH:mm') }}</span>
          </template>
        </b-table>
      </div>

      <hr />

      <!-- Top-up form -->
      <p class="font-weight-bold mb-3">Add Funds</p>

      <b-row>
        <b-col md="4">
          <b-form-group label="Amount (KES)">
            <b-form-input
              v-model.number="form.amount"
              type="number"
              min="1"
              placeholder="e.g. 2000"
              :state="errors.amount ? false : null"
            />
            <b-form-invalid-feedback>{{ errors.amount }}</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col md="5">
          <b-form-group label="Note (optional)">
            <b-form-input
              v-model="form.note"
              placeholder="e.g. Cash received at desk"
            />
          </b-form-group>
        </b-col>
        <b-col md="3" class="d-flex align-items-end pb-3">
          <b-button
            variant="primary"
            :disabled="loading || !form.amount"
            @click="topup"
          >
            <b-spinner v-if="loading" class="mr-1" />
            Load Wallet
          </b-button>
        </b-col>
      </b-row>

    </div>

  </b-modal>
</template>

<script>
export default {
  name: 'LoadCustomerWalletModal',

  data() {
    return {
      // search
      search:        '',
      searchResults: [],
      searchTimer:   null,
      searched:      false,

      // selected customer
      customer:           null,
      balance:            0,
      activeSubscription: null,
      transactions:       [],

      // top-up form
      form: {
        amount: null,
        note:   '',
      },
      errors:  {},
      loading: false,

      // transaction table fields
      txFields: [
        { key: 'created_at',  label: 'Date' },
        { key: 'type',        label: 'Type' },
        { key: 'amount',      label: 'Amount' },
        { key: 'description', label: 'Description' },
      ],
    }
  },

  methods: {

    show() {
      this.$refs.modal.show()
    },

    resetForm() {
      this.search             = ''
      this.searchResults      = []
      this.searched           = false
      this.customer           = null
      this.balance            = 0
      this.activeSubscription = null
      this.transactions       = []
      this.form               = { amount: null, note: '' }
      this.errors             = {}
      this.loading            = false
    },

    onSearch() {
      clearTimeout(this.searchTimer)
      this.searched = false

      if (this.search.length < 2) {
        this.searchResults = []
        return
      }

      this.searchTimer = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/allcustomers', {
            params: { search: this.search },
          })
          this.searchResults = (data.data || data).slice(0, 6)
          this.searched = true
        } catch (e) {
          console.error('Customer search failed', e)
        }
      }, 300)
    },

    async selectCustomer(c) {
      this.customer      = c
      this.searchResults = []
      this.search        = ''
      this.loading       = true

      try {
        const [walletRes, txRes, subRes] = await Promise.all([
          axios.get(`/api/subscriptions/wallet/${c.id}`),
          axios.get(`/api/subscriptions/wallet/${c.id}/transactions`),
          axios.get('/api/subscriptions', {
            params: { customer_id: c.id, status: 'active' },
          }),
        ])

        this.balance      = parseFloat(walletRes.data.balance || 0)
        this.transactions = txRes.data.data || txRes.data || []

        const subs = subRes.data?.subscriptions?.data || []
        this.activeSubscription = subs.length ? subs[0] : null

      } catch (e) {
        console.error('Failed to load wallet data', e)
      } finally {
        this.loading = false
      }
    },

    async topup() {
      this.errors = {}

      if (!this.form.amount || this.form.amount <= 0) {
        this.errors.amount = 'Please enter a valid amount.'
        return
      }

      this.loading = true

      try {
        await axios.post(`/api/subscriptions/wallet/${this.customer.id}/credit`, {
          amount:      this.form.amount,
          description: this.form.note || 'Manual top-up by staff',
        })

        this.$bvToast.toast(
          `KES ${this.formatAmount(this.form.amount)} loaded to ${this.customer.full_name}'s wallet.`,
          { title: 'Wallet Loaded', variant: 'success' }
        )

        // Refresh balance and transactions
        const [walletRes, txRes] = await Promise.all([
          axios.get(`/api/subscriptions/wallet/${this.customer.id}`),
          axios.get(`/api/subscriptions/wallet/${this.customer.id}/transactions`),
        ])

        this.balance      = parseFloat(walletRes.data.balance || 0)
        this.transactions = txRes.data.data || txRes.data || []
        this.form         = { amount: null, note: '' }

      } catch (e) {
        this.$bvToast.toast(
          e.response?.data?.message || 'Failed to load wallet.',
          { title: 'Error', variant: 'danger' }
        )
      } finally {
        this.loading = false
      }
    },

    formatAmount(v) {
      return parseFloat(v || 0).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    formatDate(d, fmt = 'DD-MM-YYYY') {
      return d ? moment(d).format(fmt) : '—'
    },
  },
}
</script>
